import React, { createContext, useContext, useEffect, useState } from 'react';
import { getTheme } from '../api/configHelper';
import themes from '../themes';

// Mapping from old DaisyUI variable names to new ones
const variableMapping = {
  '--b1': '--base-100',
  '--b2': '--base-200',
  '--b3': '--base-300',
  '--bc': '--base-content',
  '--p': '--primary',
  '--pf': '--primary-focus',
  '--pc': '--primary-content',
  '--s': '--secondary',
  '--sf': '--secondary-focus',
  '--sc': '--secondary-content',
  '--n': '--neutral',
  '--nf': '--neutral-focus',
  '--nc': '--neutral-content',
  '--rounded-box': '--rounded-box',
  '--rounded-btn': '--rounded-btn',
};

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('light');

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const themeName = await getTheme();
        console.log('🎨 Applying theme:', themeName);
        setCurrentTheme(themeName);
        applyTheme(themeName);
      } catch (error) {
        console.error('Failed to load theme:', error);
        console.log('🎨 Falling back to Light theme');
        applyTheme('Light');
      }
    };
    loadTheme();
  }, []);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '255, 255, 255';
  };

  const applyTheme = (themeName) => {
    console.log('🎨 Applying theme:', themeName);
    console.log('🎨 Available themes:', Object.keys(themes));
    if (themes[themeName]) {
      localStorage.setItem('theme', themeName);
      document.documentElement.setAttribute('data-theme', themeName);
      console.log('🎨 data-theme set to:', document.documentElement.getAttribute('data-theme'));
      // Apply CSS custom properties from themes.js, mapping old names to new
      const themeVars = themes[themeName];
      localStorage.setItem('themeVars', JSON.stringify(themeVars));
      console.log('🎨 Theme variables:', themeVars);
      Object.entries(themeVars).forEach(([oldKey, value]) => {
        const newKey = variableMapping[oldKey] || oldKey;
        document.documentElement.style.setProperty(newKey, value);
        console.log(`🎨 Set ${newKey}: ${value}`);
        // If it's a color variable, also set RGB version for rgba usage
        if (newKey.startsWith('--bg-') || newKey.startsWith('--base-') || newKey.startsWith('--primary') || newKey.startsWith('--secondary') || newKey.startsWith('--accent')) {
          const rgbValue = hexToRgb(value);
          document.documentElement.style.setProperty(`${newKey}-rgb`, rgbValue);
          console.log(`🎨 Set ${newKey}-rgb: ${rgbValue}`);
        }
      });
      console.log('🎨 CSS properties applied');
      // Check some key variables
      const bgPrimary = getComputedStyle(document.documentElement).getPropertyValue('--bg-primary');
      const base100 = getComputedStyle(document.documentElement).getPropertyValue('--base-100');
      console.log('🎨 --bg-primary:', bgPrimary);
      console.log('🎨 --base-100:', base100);
      console.log('🎨 Theme applied successfully');
    } else {
      console.warn('🎨 Theme not found:', themeName);
      document.documentElement.setAttribute('data-theme', 'light');
      console.log('🎨 Fallback to light');
    }
  };

  const changeTheme = (themeName) => {
    setCurrentTheme(themeName);
    applyTheme(themeName);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme }}>
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;